import React, { useState, useEffect, useCallback } from 'react';
import { Button, Flex, Modal, Typography, Divider } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import './index.scss';
import { RequestConfirmModalProps } from 'interfaces/components/requestConfirmModal';
import { useUpdateConfirmModal } from 'hooks/useConfirmModal';

const { Text, Title } = Typography;

export const initialStatus = {
  isCanceled: false,
  isPending: false,
  isSuccess: false,
  closePending: false,
};

const RequestConfirmModal = ({
  translate,
  icon,
  title,
  subtitle,
  transactionDetails,
}: RequestConfirmModalProps) => {
  const { modalStatus, setUpdateStatus } = useUpdateConfirmModal();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleButtonClick = useCallback(() => {
    modalStatus?.onCloseDrawer?.();
    setUpdateStatus(initialStatus);
    setIsModalOpen(false);
  }, [modalStatus, setUpdateStatus]);

  useEffect(() => {
    if (modalStatus?.closePending) {
      handleButtonClick();
    }

    if (modalStatus?.isPending) {
      setIsModalOpen(true);
    }
  }, [modalStatus, handleButtonClick]);

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleButtonClick}
      footer={null}
      width={480}
      closeIcon={false}
      closable={true}
      centered={true}
      className="transaction-confirm-modal"
    >
      <Flex vertical className="modal-container">
        <Flex
          vertical
          align="center"
          justify="center"
          className="modal-header"
        >
          <div className="icon-container">
            {icon || <CheckCircleFilled className="success-icon" />}
          </div>
          <Title level={4} className="modal-title">
            {title}
          </Title>
          {subtitle && (
            <Text className="modal-subtitle">{subtitle}</Text>
          )}
        </Flex>

        {transactionDetails &&
          Object.keys(transactionDetails).some(
            (key) => key !== 'TotalAmount' && transactionDetails[key],
          ) && (
            <>
              <Divider className="modal-divider" />
              <div className="transaction-details">
                <Flex vertical gap={4}>
                  {transactionDetails.TransactionID && (
                    <DetailRow
                      label={translate?.('Transaction ID')}
                      value={transactionDetails.TransactionID}
                    />
                  )}
                  {transactionDetails.Amount && (
                    <DetailRow
                      label={translate?.('Amount')}
                      value={transactionDetails.Amount}
                      highlight
                    />
                  )}
                  {transactionDetails.Fees && (
                    <DetailRow
                      label={translate?.('Fees')}
                      value={transactionDetails.Fees}
                    />
                  )}
                  {transactionDetails.ExchangeFees && (
                    <DetailRow
                      label={translate?.('Exchange Fees')}
                      value={transactionDetails.ExchangeFees}
                    />
                  )}
                  {transactionDetails.Taxes && (
                    <DetailRow
                      label={translate?.('Taxes')}
                      value={transactionDetails.Taxes}
                    />
                  )}
                  {transactionDetails.ExchangeRate && (
                    <DetailRow
                      label={translate?.('Exchange Rate')}
                      value={transactionDetails.ExchangeRate}
                    />
                  )}
                </Flex>
              </div>
            </>
          )}

        {transactionDetails?.TotalAmount && (
          <>
            <Divider className="modal-divider" />
            <div className="total-section">
              <DetailRow
                label={translate?.('Total Amount')}
                value={transactionDetails.TotalAmount}
                highlight
                large
              />
            </div>
          </>
        )}

        <Flex vertical className="action-container">
          <Button
            type="primary"
            size="large"
            onClick={handleButtonClick}
            className="confirm-button"
          >
            {translate?.('Ok')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
  large?: boolean;
}> = ({ label, value, highlight, large }) => (
  <Flex
    justify="space-between"
    align="center"
    className={`detail-row ${large ? 'large' : ''}`}
  >
    <Text className={`label ${large ? 'large-label' : ''}`}>
      {label}
    </Text>
    <Text
      className={`value ${highlight ? 'highlight' : ''} ${large ? 'large-value' : ''}`}
    >
      {value}
    </Text>
  </Flex>
);

export default RequestConfirmModal;
